// Substrate-need inference for `cloudgrid init` gridify mode (BL621 — Decision
// 042 Mode 1 §init behavior b; kickoff Step 3: "detect needs: candidates —
// postgres? redis? vector? mongo?").
//
// This maps a small, conservative set of well-known database / cache / vector
// client libraries — read from an already-gathered BL592 `RepoSnapshot` — onto
// the cloudgrid.yaml v2 `needs:` vocabulary (see packages/shared validate.ts:
// the nine semantic needs). It returns *candidates only*: gridify shows them to
// the user for confirmation before any yaml is written.
//
// It is deliberately NOT a BL592 knowledge-base signature. Those signatures
// describe build/runtime pitfalls and runtime-need words (`node` / `python`),
// live in packages/shared, and are matched by `findMatchingSignatures`. The
// substrate map here is CLI-side, narrow, and explicit so it stays easy to read
// and audit. New library families are added by editing `RULES` below — no
// engine change.

import type { RepoSnapshot } from '../../../shared/src/drop-detection/index.js';

/** The substrate needs gridify can infer. A subset of the v2 need vocabulary. */
export type SubstrateNeed = 'database' | 'cache' | 'vector';

interface NeedRule {
  /** The cloudgrid.yaml v2 need-word this rule proposes. */
  need: SubstrateNeed;
  /** package.json dependency keys (dependencies or devDependencies) that imply it. */
  npm: string[];
  /** requirements.txt package names (lowercase) that imply it. */
  py: string[];
}

/**
 * Library → need map. Order is the surfacing order. Kept conservative: only
 * clients that unambiguously talk to a managed substrate the platform offers.
 * Notably absent: hosted-SaaS SDKs (Pinecone, Upstash, PlanetScale) — those are
 * external services the user wires with their own secret, not a platform need.
 */
const RULES: readonly NeedRule[] = [
  {
    need: 'database',
    npm: [
      'pg',
      'postgres',
      'mysql',
      'mysql2',
      'mongodb',
      'mongoose',
      'prisma',
      '@prisma/client',
      'typeorm',
      'sequelize',
      'knex',
    ],
    py: [
      'psycopg2',
      'psycopg2-binary',
      'psycopg',
      'asyncpg',
      'pymongo',
      'sqlalchemy',
      'mysqlclient',
    ],
  },
  {
    need: 'cache',
    npm: ['redis', 'ioredis'],
    py: ['redis'],
  },
  {
    need: 'vector',
    npm: ['pgvector'],
    py: ['pgvector'],
  },
];

/** Merge `dependencies` + `devDependencies` keys from a parsed package.json. */
function dependencyKeys(repo: RepoSnapshot): Set<string> {
  const keys = new Set<string>();
  const pkg = repo.package_json;
  if (!pkg) return keys;
  for (const map of [pkg.dependencies, pkg.devDependencies]) {
    if (map && typeof map === 'object') {
      for (const key of Object.keys(map)) keys.add(key);
    }
  }
  return keys;
}

/**
 * Parse requirements.txt into a lowercase set of bare package names, dropping
 * version specifiers, extras, markers, comments, and `-r`/option lines. Mirrors
 * the knowledge-base's parser so the two surfaces read the same files the same
 * way.
 */
function requirementNames(repo: RepoSnapshot): Set<string> {
  const names = new Set<string>();
  const contents = repo.requirements_txt;
  if (!contents) return names;
  for (const line of contents.split(/\r?\n/)) {
    const stripped = line.split('#', 1)[0].trim();
    if (stripped === '' || stripped.startsWith('-')) continue;
    const name = stripped.split(/[=<>!~;[\s]/, 1)[0].trim().toLowerCase();
    if (name) names.add(name);
  }
  return names;
}

/**
 * Infer the substrate `needs:` candidates for a repo, in `RULES` order, with no
 * duplicates. Pure and deterministic — same snapshot in, same needs out.
 */
export function inferNeeds(repo: RepoSnapshot): SubstrateNeed[] {
  const deps = dependencyKeys(repo);
  const reqs = requirementNames(repo);
  const out: SubstrateNeed[] = [];
  for (const rule of RULES) {
    const hit =
      rule.npm.some((d) => deps.has(d)) ||
      rule.py.some((r) => reqs.has(r));
    if (hit) out.push(rule.need);
  }
  return out;
}
